import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, HelpCircle } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { useCmsContent } from '../hooks/useCmsContent';
import { useLanguage } from '../contexts/LanguageContext';
import { parseHighlight } from '../utils/richText';

const DEFAULT_FAQS = [
  { question: 'How do I book classes?', answer: 'Start with the free level test or a trial lesson. After we understand your goals and current level, we confirm the best package and available schedule with you directly.', color: 'border-l-[#D72B2B]' },
  { question: 'Do you offer online lessons?', answer: 'Yes. Online lessons are delivered live through Google Meet. All you need is a stable internet connection and a computer or tablet with a camera and microphone.', color: 'border-l-[#4CAF50]' },
  { question: 'Do you offer in-person lessons?', answer: 'Yes, depending on availability and location. Contact us to discuss in-person lesson options in your area.', color: 'border-l-[#F5A623]' },
  { question: 'How does the level test work?', answer: 'Our level test is a quick online assessment that evaluates your grammar, vocabulary, reading, and listening skills. It takes about 5-10 minutes and gives you a practical understanding of your current English level, helping us recommend the best learning plan for you.', color: 'border-l-[#1A2E6E]' },
  { question: 'How do I choose the right package?', answer: "We recommend taking the free level test first, which will help us understand your current level and suggest an appropriate package. You can also book a trial lesson where we can discuss your goals and recommend the best option for you.", color: 'border-l-[#D72B2B]' },
  { question: 'Can I reschedule lessons?', answer: 'Yes. Lessons can be rescheduled when requested through the booking agenda at least 24 hours in advance. If the student does not notify us with 24 hours’ notice, the missed lesson will not be eligible for a refund or replacement class.', color: 'border-l-[#4CAF50]' },
  { question: 'Do you teach children and teens?', answer: 'We currently teach students aged 16 and above. At the moment, we do not offer classes for younger children.', color: 'border-l-[#F5A623]' },
  { question: 'Do you provide Business English?', answer: 'Yes. We offer Business English classes focused on real professional communication, including meetings, presentations, emails, negotiations, and interviews. These classes can be tailored to your specific industry or profession.', color: 'border-l-[#1A2E6E]' },
  { question: 'What qualifications do your teachers have?', answer: 'Our teacher is CELTA (Cambridge) certified, holds a Master’s degree in TESOL, and is currently pursuing a Doctorate focused on teaching English in the AI era, combining internationally recognized credentials with cutting-edge research in language education.', color: 'border-l-[#D72B2B]' },
  { question: 'How long does it take to see results?', answer: 'Progress varies depending on your starting level, goals, and how much time you dedicate to learning. Most students notice improvements within the first few weeks, particularly in confidence and fluency.', color: 'border-l-[#4CAF50]' },
  { question: 'What materials do you use?', answer: 'We use a combination of high-quality course materials, custom resources, and real-world content tailored to your interests and goals. All materials are provided through Google Classroom.', color: 'border-l-[#F5A623]' },
  { question: 'How do I pay for lessons?', answer: 'Payment options are confirmed during enrollment based on the selected package and location. Contact us and we will share the available payment methods and next steps.', color: 'border-l-[#1A2E6E]' }
];

const accentColors = ['border-l-[#D72B2B]', 'border-l-[#4CAF50]', 'border-l-[#F5A623]', 'border-l-[#1A2E6E]'];

export const FAQ = () => {
  const cms = useCmsContent('faq');
  const { t, tList } = useLanguage();

  const heroBadge    = cms.get('hero', 'badge',  t('faq.hero.badge'));
  const heroTitle    = cms.get('hero', 'title',  t('faq.hero.title'));
  const heroBody     = cms.get('hero', 'body',   t('faq.hero.body'));
  const ctaTitle     = cms.get('cta', 'title',   t('faq.ctaSection.title'));
  const ctaBody      = cms.get('cta', 'body',    t('faq.ctaSection.body'));
  const ctaPrimary   = cms.get('cta', 'cta_primary',   t('faq.ctaSection.primary'));
  const ctaSecondary = cms.get('cta', 'cta_secondary', t('faq.ctaSection.secondary'));

  const localizedFaqs = tList('faq.faqList');
  const cmsFaqs = cms.getList('faq_items', []);
  const faqs = cmsFaqs.length > 0
    ? cmsFaqs.map((f, i) => ({ ...DEFAULT_FAQS[i] || {}, color: accentColors[i % 4], question: f.question || '', answer: f.answer || '' }))
    : DEFAULT_FAQS.map((f, i) => ({ ...f, question: localizedFaqs[i]?.question || f.question, answer: localizedFaqs[i]?.answer || f.answer }));

  return (
    <main className="pt-24" data-testid="faq-page">
      {/* Hero Section */}
      <section className="section-padding bg-cream-gradient">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="badge-orange mb-4 inline-block">
              {heroBadge}
            </span>
            <h1 className="font-sora text-4xl md:text-5xl font-bold text-[#1A2E6E] mb-6">
              {parseHighlight(heroTitle)}
            </h1>
            <p className="text-lg text-[#1A2E6E]/70 leading-relaxed">
              {heroBody}
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="section-padding bg-warm-texture">
        <div className="container-custom max-w-3xl">
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <AccordionItem
                  value={`item-${index}`}
                  className={`glass-card rounded-xl px-6 border-none border-l-4 ${faq.color}`}
                  data-testid={`faq-item-${index}`}
                >
                  <AccordionTrigger className="text-left font-sora font-semibold text-[#1A2E6E] hover:text-[#1A2E6E] hover:no-underline py-6">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#1A2E6E]/70 pb-6 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              </motion.div>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-paper-lines">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card p-12 rounded-3xl text-center max-w-3xl mx-auto border-t-4 border-t-[#4CAF50]"
          >
            <div className="w-16 h-16 rounded-2xl bg-[#4CAF50]/10 flex items-center justify-center mx-auto mb-6">
              <HelpCircle size={32} className="text-[#4CAF50]" />
            </div>
            <h2 className="font-sora text-3xl font-bold text-[#1A2E6E] mb-4">
              {parseHighlight(ctaTitle)}
            </h2>
            <p className="text-[#1A2E6E]/70 mb-8">
              {ctaBody}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                className="btn-green inline-flex items-center gap-2"
                data-testid="faq-contact-btn"
              >
                {ctaPrimary} <ArrowRight size={18} />
              </Link>
              <Link
                to="/book-trial"
                className="btn-secondary"
                data-testid="faq-book-trial-btn"
              >
                {ctaSecondary}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};
